/**
 * Habillages repris des sites publics le 05/10/2026.
 * Café Crème Club : --primary #0033FF, fond #FFFEFA, wordmark blanc et monogramme /favicon-cc.png.
 * Launchpad : --accent #E4FB4D, encre #16150F, fond de page neutre.
 * Le logo est le carré #E4FB4D avec le L noir, fond compris.
 * Les textes commerciaux ne sont pas ici.
 *
 * DNS attendu, sans toucher au VPS : les deux noms pointent vers le même processus,
 * et le proxy transmet l'en-tête Host (ou X-Forwarded-Host).
 *   rdv.cafe-creme.club.  CNAME  <nom public du serveur>
 *   rdv.launch-pad.fr.     CNAME  <nom public du serveur>
 * Si le proxy n'a pas de nom, un enregistrement A (ou AAAA) vers son adresse,
 * la même pour les deux. On ne fixe pas cette adresse ici.
 */

export type BrandSlug = "cafe-creme-club" | "launchpad";

export type BrandSkin = {
  slug: BrandSlug;
  name: string;
  host: string;
  brandColor: string;
  darkBrandColor: string;
  background: string;
  ink: string;
  logoBackdrop: string;
  /** Encre du bandeau posé sur la couleur de marque. */
  headerInk: string;
  /** Couleur du calendrier. Le jaune Launchpad disparaîtrait sur le fond blanc. */
  bookerColor: string;
  markUrl: string;
  wordmarkUrl: string | null;
  source: string;
};

export const BRAND_HEADER = "x-rdv-brand";

export const BRAND_SKINS: Record<BrandSlug, BrandSkin> = {
  "cafe-creme-club": {
    slug: "cafe-creme-club",
    name: "Café Crème Club",
    host: "rdv.cafe-creme.club",
    brandColor: "#0033FF",
    darkBrandColor: "#0029CC",
    background: "#FFFEFA",
    ink: "#1C1A17",
    logoBackdrop: "#0033FF",
    headerInk: "#FFFFFF",
    bookerColor: "#0033FF",
    markUrl: "/brands/cafe-creme-club/mark.png",
    wordmarkUrl: "/brands/cafe-creme-club/wordmark.svg",
    source: "https://cafe-creme.club",
  },
  launchpad: {
    slug: "launchpad",
    name: "Launchpad",
    host: "rdv.launch-pad.fr",
    brandColor: "#E4FB4D",
    darkBrandColor: "#E4FB4D",
    background: "#F7F7F4",
    ink: "#16150F",
    logoBackdrop: "#E4FB4D",
    headerInk: "#16150F",
    bookerColor: "#16150F",
    markUrl: "/brands/launchpad/logo.png",
    wordmarkUrl: null,
    source: "https://launch-pad.fr",
  },
};

export type BrandRoute =
  | { action: "next"; brand: BrandSkin | null }
  | { action: "rewrite"; pathname: string; brand: BrandSkin }
  | { action: "not-found"; brand: BrandSkin };

const PASSTHROUGH_PREFIXES = ["/api", "/_next", "/brands", "/embed"];

export function brandFromHost(raw: string | null | undefined): BrandSkin | null {
  if (!raw) return null;
  const host = raw.split(",")[0]?.trim().toLowerCase().replace(/:\d+$/, "");
  if (!host) return null;
  return Object.values(BRAND_SKINS).find((skin) => skin.host === host) ?? null;
}

export function brandBySlug(slug: string | null | undefined): BrandSkin | null {
  if (slug === "cafe-creme-club" || slug === "launchpad") return BRAND_SKINS[slug];
  return null;
}

/** Compte sans événement (reset, vérif) : Café Crème Club. */
export function brandAppName(brand: BrandSkin | BrandSlug | null | undefined): string {
  if (typeof brand === "string") {
    return brandBySlug(brand)?.name ?? BRAND_SKINS["cafe-creme-club"].name;
  }
  return brand?.name ?? BRAND_SKINS["cafe-creme-club"].name;
}

/** Nom d'affichage From ; même source que brandAppName. */
export function brandEmailFromName(brand: BrandSkin | BrandSlug | null | undefined): string {
  return brandAppName(brand);
}

type HeaderReader = { get(name: string): string | null | undefined };

function asReader(headers: HeaderReader | Record<string, string | string[] | undefined>): HeaderReader {
  if (typeof (headers as HeaderReader).get === "function") return headers as HeaderReader;
  return {
    get(name: string) {
      const value = (headers as Record<string, string | string[] | undefined>)[name];
      if (Array.isArray(value)) return value[0];
      return value;
    },
  };
}

/**
 * Origine publique de chaque marque.
 * Prod : rien à régler, c'est https://<host>.
 * Recette : RDV_BRAND_ORIGINS="cafe-creme-club=http://localhost:3002,launchpad=http://localhost:3003".
 * Le serveur lit la variable ; le navigateur la lit dans <meta name="rdv-brand-origins"> (posé par app/layout.tsx).
 */
export const BRAND_ORIGINS_ENV = "RDV_BRAND_ORIGINS";
export const BRAND_ORIGINS_META = "rdv-brand-origins";

export function parseBrandOrigins(raw: string | null | undefined): Partial<Record<BrandSlug, string>> {
  const result: Partial<Record<BrandSlug, string>> = {};
  if (!raw) return result;
  for (const part of raw.split(",")) {
    const [slug, origin] = part.split("=").map((value) => value?.trim());
    if ((slug === "cafe-creme-club" || slug === "launchpad") && origin) {
      try {
        result[slug] = new URL(origin).origin;
      } catch {
        // Valeur illisible : on garde le domaine de prod.
      }
    }
  }
  return result;
}

function readBrandOriginsRaw(): string | undefined {
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    return document.querySelector(`meta[name="${BRAND_ORIGINS_META}"]`)?.getAttribute("content") ?? undefined;
  }
  return typeof process !== "undefined" ? process.env[BRAND_ORIGINS_ENV] : undefined;
}

export function brandPublicOrigin(brand: BrandSkin, raw: string | null | undefined = readBrandOriginsRaw()): string {
  return parseBrandOrigins(raw)[brand.slug] ?? `https://${brand.host}`;
}

export function brandPublicOrigins(raw?: string | null): string[] {
  return Object.values(BRAND_SKINS).map((brand) =>
    brandPublicOrigin(brand, raw === undefined ? readBrandOriginsRaw() : raw)
  );
}

export function brandOriginForSlug(slug: string | null | undefined): string | null {
  const brand = brandBySlug(slug);
  return brand ? brandPublicOrigin(brand) : null;
}

/** Origine de la marque d'un type d'événement (metadata.brandSlug), sinon null. */
export function brandOriginForEventMetadata(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== "object") return null;
  return brandOriginForSlug((metadata as { brandSlug?: string | null }).brandSlug);
}

export function brandFromHeaders(
  headers: HeaderReader | Record<string, string | string[] | undefined>,
  options?: { trustBrandHeader?: boolean }
) {
  const read = asReader(headers);
  // /api is outside the proxy matcher: a client can send x-rdv-brand. Ignore it there.
  if (options?.trustBrandHeader !== false) {
    const explicit = read.get(BRAND_HEADER);
    const fromExplicit = brandBySlug(explicit) ?? brandFromHost(explicit);
    if (fromExplicit) return fromExplicit;
  }
  return brandFromHost(read.get("x-forwarded-host")) ?? brandFromHost(read.get("host"));
}

export function routeBrandRequest(host: string | null | undefined, pathname: string): BrandRoute {
  const brand = brandFromHost(host);
  if (!brand) return { action: "next", brand: null };
  if (PASSTHROUGH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return { action: "next", brand };
  }
  const teamPath = pathname.match(/^\/team\/([^/]+)(?:\/.*)?$/);
  if (teamPath && teamPath[1] !== brand.slug) {
    return { action: "not-found", brand };
  }
  return { action: "next", brand };
}

/**
 * Cible de la 307 sur `/` d'un hôte de marque. `null` : laisser le home Cal.diy
 * (onboarding ou /event-types). Un visiteur authentifié ne va jamais à /auth/login :
 * cette page renvoie déjà `/`, ce qui ferait une boucle tant que l'équipe n'existe pas.
 */
export function brandHomePath(
  brand: BrandSkin | null,
  existingTeamSlug: string | null | undefined,
  authenticated = false
): string | null {
  if (!brand) return null;
  if (authenticated) return null;
  if (existingTeamSlug === brand.slug) return `/team/${brand.slug}`;
  return "/auth/login";
}

export function appHomeDestination(input: {
  brand: BrandSkin | null;
  existingTeamSlug?: string | null;
  authenticated: boolean;
  onboardingPath?: string | null;
}): string {
  const branded = brandHomePath(input.brand, input.existingTeamSlug, input.authenticated);
  if (branded) return branded;
  if (!input.authenticated) return "/auth/login";
  if (input.onboardingPath) return input.onboardingPath;
  return "/event-types";
}

export function visibleOnBrand<T extends { metadata?: { brandSlug?: string | null } | null }>(
  events: T[],
  brand: BrandSkin | null
): T[] {
  if (!brand) return events;
  return events.filter((event) => event.metadata?.brandSlug === brand.slug);
}
